<template>
	<NuxtLink v-if="fullPath" :to="{ path: fullPath }" @click.native="handleSportClick" class="card">
		<span class="card__image">
			<img v-if="sportIcon" :src="sportIcon" :alt="$t(`SPORTS_WEBAPP_SPORT_TYPE_${sport}`)" />
		</span>

		<span class="card__text">{{ $t(`SPORTS_WEBAPP_SPORT_TYPE_${sport}`) }}</span>
	</NuxtLink>
</template>

<script setup lang="ts">
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getOverviewUrlBySport } from '@/routing/sports';

import type { SportLocale } from '@/features/sports/types/enums';
import { ObjectType, SportType } from '@/@types/graphql-types';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const props = defineProps<{ sport: SportType }>();

const languageStore = storeToRefs(useLanguageStore());

const sportIcon = computed(() => {
	return `/${ASSETS_DIR}/img/sports/icons/sport/${props.sport.toLowerCase()}.png`;
});

const fullPath = computed(() => getOverviewUrlBySport(props.sport, languageStore.webLocale.value as SportLocale));

function handleSportClick() {
	const sportContext = new SnowplowSportContext(props.sport, ObjectType.Sport);

	TrackingHelper.trackEvent('userinteraction', { action: 'sport_clicked', property: 'icon', useLastRoute: true }, [
		sportContext,
	]);
}
</script>

<style lang="scss" scoped>
@import '~@/styles/sports.scss';

.card {
	height: 100%;
	width: 80px;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	color: $color-primary-light;

	@media (min-width: 550px) {
		width: 100px;
		margin-inline-end: 16px;
	}

	&__image {
		width: 40px;
		height: 40px;
		background-color: transparent;
		border-radius: 50%;
		display: flex;
		justify-content: center;
		align-items: center;

		@media (min-width: 550px) {
			width: 80px;
			height: 80px;
			background-color: $color-primary-pale-dark;
			padding: 16px;
		}
	}

	&__text {
		@include typo-sport-medium;

		margin-top: 12px;
		text-align: center;
		font-size: 14px;

		@media (min-width: 550px) {
			margin-top: 14px;
			font-size: 20px;
			color: $color-neutral-neutral-light;
		}

		// this applies "line-clamp: 2"
		overflow: hidden;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
	}
}
</style>
